import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { formatCurrency, formatDate, formatCommissionRate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus,
  DollarSign,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileText,
  Eye,
  Edit,
  Download,
} from 'lucide-react';
import CommissionClaimForm from './CommissionClaimForm';
import ErrorBoundary from '../../components/common/ErrorBoundary';

const statusColors = {
  draft: 'bg-gray-500',
  submitted: 'bg-blue-500',
  under_review: 'bg-yellow-500',
  revision_requested: 'bg-orange-500',
  pic_approved: 'bg-cyan-500',
  admin_approved: 'bg-indigo-500',
  approved: 'bg-green-500',
  paid: 'bg-emerald-600',
  rejected: 'bg-red-500',
};

const statusLabels = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under Review',
  revision_requested: 'Needs Revision',
  pic_approved: 'PIC Approved',
  admin_approved: 'Admin Approved',
  approved: 'Fully Approved',
  paid: 'Paid',
  rejected: 'Rejected',
};

const AgentCommissionClaims = () => {
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showNewClaimForm, setShowNewClaimForm] = useState(false);
  const [editingClaim, setEditingClaim] = useState(null);
  const [loadingClaimDetails, setLoadingClaimDetails] = useState(false);

  useEffect(() => {
    loadClaims();
  }, []);

  const loadClaims = async () => {
    try {
      const res = await api.get('/commission-claims', { params: { limit: 500 } });
      // New API: paginated dict {items, total, page, limit}; older list shape kept as fallback
      setClaims(Array.isArray(res.data) ? res.data : (res.data?.items || []));
    } catch (error) {
      console.error('Error loading claims:', error);
      toast.error('Failed to load claims');
    } finally {
      setLoading(false);
    }
  };

  const loadClaimDetails = async (claimId) => {
    setLoadingClaimDetails(true);
    try {
      const res = await api.get(`/commission-claims/${claimId}`);
      setEditingClaim(res.data);
    } catch (error) {
      toast.error('Failed to load claim details');
    } finally {
      setLoadingClaimDetails(false);
    }
  };

  const handleDownloadStatement = async (claim) => {
    try {
      const res = await api.get(`/commission-claims/${claim.id}/commission-statement`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `commission_statement_${claim.claim_ref || claim.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      const msg = e.response?.status === 404
        ? 'Statement not yet generated. Submit the claim first.'
        : e.response?.data?.detail || 'Failed to download statement';
      toast.error(msg);
    }
  };

  const handleEditClaim = (claim) => {
    // Load full claim details including revision comments
    loadClaimDetails(claim.id);
  };

  const handleClaimSuccess = () => {
    setShowNewClaimForm(false);
    setEditingClaim(null);
    loadClaims();
    toast.success('Claim saved successfully');
  };

  const filteredClaims = claims.filter((c) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'action_needed') return ['draft', 'revision_requested'].includes(c.status);
    if (activeTab === 'in_progress') return ['submitted', 'under_review'].includes(c.status);
    return c.status === activeTab;
  });

  const totalPending = claims.filter(c => ['submitted', 'under_review', 'approved'].includes(c.status))
    .reduce((sum, c) => sum + (c.gross_commission || c.total_commission || 0), 0);
  const totalPaid = claims.filter(c => c.status === 'paid')
    .reduce((sum, c) => sum + (c.gross_commission || c.total_commission || 0), 0);
  const actionNeededCount = claims.filter(c => ['draft', 'revision_requested'].includes(c.status)).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agent-commission-claims">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commission Claims</h1>
          <p className="text-gray-500">Submit and track your commission claims</p>
        </div>
        <Button onClick={() => setShowNewClaimForm(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-1" /> New Claim
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Paid</p>
                <p className="text-xl font-bold text-emerald-600">{formatCurrency(totalPaid)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-xl font-bold text-yellow-600">{formatCurrency(totalPending)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Claims</p>
                <p className="text-xl font-bold">{claims.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {actionNeededCount > 0 && (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-orange-700">Action Needed</p>
                  <p className="text-xl font-bold text-orange-600">{actionNeededCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Claims Table */}
      <Card>
        <CardContent className="p-0">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="border-b px-4">
              <TabsList className="h-12 bg-transparent">
                <TabsTrigger value="all">All</TabsTrigger>
                {actionNeededCount > 0 && (
                  <TabsTrigger value="action_needed" className="text-orange-600">
                    Action Needed ({actionNeededCount})
                  </TabsTrigger>
                )}
                <TabsTrigger value="in_progress">In Progress</TabsTrigger>
                <TabsTrigger value="paid">Paid</TabsTrigger>
                <TabsTrigger value="rejected">Rejected</TabsTrigger>
              </TabsList>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ref</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>SPA Price</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Co-Broke</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClaims.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                      No claims found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredClaims.map((claim) => (
                    <TableRow key={claim.id}>
                      <TableCell className="font-mono text-sm">{claim.claim_ref}</TableCell>
                      <TableCell>{claim.project_name}</TableCell>
                      <TableCell>{claim.unit_number}</TableCell>
                      <TableCell>{formatCurrency(claim.spa_price)}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{formatCurrency(claim.gross_commission || claim.total_commission || 0)}</p>
                          <p className="text-xs text-gray-500">{formatCommissionRate(claim)}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {claim.is_cobroke ? (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700">Yes</Badge>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusColors[claim.status]} text-white`}>
                          {statusLabels[claim.status]}
                        </Badge>
                        {claim.status === 'revision_requested' && (
                          <p className="text-xs text-orange-600 mt-1">
                            {claim.revision_count || 1}x revision
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {formatDate(claim.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost">
                            <Eye className="h-4 w-4" />
                          </Button>
                          {['draft', 'revision_requested'].includes(claim.status) && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleEditClaim(claim)}
                              disabled={loadingClaimDetails}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {claim.status === 'paid' && (
                            <Button size="sm" variant="ghost">
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                          {claim.status !== 'draft' && (
                            <button
                              type="button"
                              onClick={() => handleDownloadStatement(claim)}
                              title="Download Commission Statement (PDF)"
                              data-testid={`download-stmt-${claim.id}`}
                              className="inline-flex items-center justify-center h-8 w-8 rounded text-emerald-600 hover:bg-emerald-50 transition"
                            >
                              <FileText className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Tabs>
        </CardContent>
      </Card>

      {/* New Claim Dialog - Full width for comprehensive form */}
      <Dialog open={showNewClaimForm} onOpenChange={setShowNewClaimForm}>
        <DialogContent className="max-w-6xl w-[95vw] max-h-[95vh] overflow-y-auto p-6">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Plus className="h-5 w-5 text-blue-600" />
              New Commission Claim
            </DialogTitle>
          </DialogHeader>
          <ErrorBoundary>
            <CommissionClaimForm
              onSuccess={handleClaimSuccess}
              onCancel={() => setShowNewClaimForm(false)}
            />
          </ErrorBoundary>
        </DialogContent>
      </Dialog>

      {/* Edit Claim Dialog - Full width for comprehensive form */}
      <Dialog open={!!editingClaim} onOpenChange={(open) => !open && setEditingClaim(null)}>
        <DialogContent className="max-w-6xl w-[95vw] max-h-[95vh] overflow-y-auto p-6">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Edit className="h-5 w-5 text-blue-600" />
              {editingClaim?.status === 'revision_requested' ? 'Revise Commission Claim' : 'Edit Commission Claim'}
            </DialogTitle>
          </DialogHeader>
          {editingClaim && (
            <ErrorBoundary>
              <CommissionClaimForm
                existingClaim={editingClaim}
                onSuccess={handleClaimSuccess}
                onCancel={() => setEditingClaim(null)}
              />
            </ErrorBoundary>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgentCommissionClaims;
